import React, { useCallback, useState } from "react";
import { alpha } from "@mui/material/styles";
import { Avatar, Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

const MAX_RECIENTES = 8;

const leer = (clave) => {
  try {
    const l = JSON.parse(localStorage.getItem(clave) || "[]");
    return Array.isArray(l) ? l.slice(0, MAX_RECIENTES) : [];
  } catch { return []; }
};
const guardar = (clave, lista) => { try { localStorage.setItem(clave, JSON.stringify(lista)); } catch { /* sin almacenamiento */ } };
const igual = (a, b) => ["nombreCliente", "estado", "codServicio", "sucursal"].every((k) => String(a[k] || "").toLowerCase() === String(b[k] || "").toLowerCase());

// Recuerda las últimas búsquedas de clientes (por navegador, bajo `clave`) para repetirlas con un clic.
// `buscar` sustituye al onSearch del SearchBar; `repetir` rellena el formulario (vía `precargar`) y busca.
export function useBusquedasRecientes(clave, search) {
  const [recientes, setRecientes] = useState(() => leer(clave));
  const [precargar, setPrecargar] = useState(null);

  const buscar = useCallback((filtros) => {
    const nombre = (filtros.nombreCliente || "").trim();
    if (nombre) {
      const nueva = { nombreCliente: nombre, estado: filtros.estado || "Activo", codServicio: filtros.codServicio || "", sucursal: filtros.sucursal || "" };
      setRecientes((prev) => {
        const lista = [nueva, ...prev.filter((r) => !igual(r, nueva))].slice(0, MAX_RECIENTES);
        guardar(clave, lista);
        return lista;
      });
    }
    search(filtros);
  }, [clave, search]);

  const repetir = (r) => {
    setPrecargar({ ...r, n: Date.now() });
    buscar(r);
  };
  const quitar = (r) => setRecientes((prev) => { const lista = prev.filter((x) => !igual(x, r)); guardar(clave, lista); return lista; });
  const limpiar = () => { guardar(clave, []); setRecientes([]); };

  return { recientes, precargar, buscar, repetir, quitar, limpiar };
}

// Panel que se muestra antes de la primera búsqueda: ayuda breve + chips de las últimas búsquedas.
export default function BusquedasRecientes({ titulo, ayuda = "Escribe la razón social, el grupo o el nombre del servicio y presiona Enter. Puedes acotar por estado, servicio o sucursal.", estadoOmitido = "Activo", recientes, servicios = [], onRepetir, onQuitar, onLimpiar }) {
  const nombreServicio = (id) => (servicios.find((x) => String(x.id_servicio) === String(id)) || {}).nombre || "";

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, display: "grid", gap: 1.5, borderStyle: "dashed", bgcolor: (t) => alpha(t.palette.primary.main, 0.035) }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Avatar sx={{ width: 36, height: 36, bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: "primary.main" }}>
          <ManageSearchIcon />
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3 }}>{titulo}</Typography>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", lineHeight: 1.4 }}>{ayuda}</Typography>
        </Box>
      </Stack>
      {recientes.length > 0 && (
        <Box sx={{ display: "grid", gap: 0.75, pt: 1.25, borderTop: 1, borderColor: "divider" }}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <HistoryIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>Últimas búsquedas</Typography>
            <Button size="small" color="inherit" onClick={onLimpiar} sx={{ textTransform: "none", fontSize: 12, ml: "auto !important", color: "text.secondary" }}>Borrar historial</Button>
          </Stack>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            {recientes.map((r) => {
              const detalle = [r.estado !== estadoOmitido ? r.estado : "", nombreServicio(r.codServicio), r.sucursal].filter(Boolean).join(" · ");
              return (
                <Chip
                  key={`${r.nombreCliente}|${r.estado}|${r.codServicio}|${r.sucursal}`}
                  clickable onClick={() => onRepetir(r)}
                  icon={<SearchIcon sx={{ fontSize: "16px !important" }} />}
                  onDelete={() => onQuitar(r)} deleteIcon={<CloseIcon />}
                  label={<span><b>{r.nombreCliente}</b>{detalle && <span style={{ opacity: 0.7 }}>{` · ${detalle}`}</span>}</span>}
                  sx={{
                    maxWidth: "100%", fontSize: 12.5, bgcolor: "background.paper", border: 1, borderColor: "divider",
                    "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.08), borderColor: "primary.main" },
                    "& .MuiChip-deleteIcon": { fontSize: 16 },
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}
    </Paper>
  );
}
